import * as React from 'react';
import { initialClientState, clientReducer } from '@actions/reducers';
import { PresentationProvider } from '@context/presentation';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { useClientQuery } from '@queries/client';
import { getAbbreviation } from '@utils/i18n';
import { openEventStream } from '@services';
import { retrieveSavedData } from '@utils/browserStorage';
import { getProfile } from '@utils/profileUtils';
import { 
    SinglePagePdf, 
    PresentationView, 
    CurrentSlide, 
    PointerImage, 
    ErrorFallback,
    IdleTimerContainer,
    ProfileCopyrightFooter } from '@components';
import { SSE_EVENTS, BROWSER_STORAGE_KEYS, PresentationType } from '@constants';
import { 
    logOpenMessage,
    clientConnectionHandler, 
    errorHandler, 
    startPresentationHandler,
    languageHandler,
    slideIdHandler, 
    pdfPageHandler,
    presentationTypeHandler,
    selectTermHandler,
    showPointerHandler,
    pointerPositionHandler,
    videoHandler,
    infoItemHandler,
    stepReviewedHandler,
    loanInputHandler,
    scrollHandler,
    endPresentationHandler
} from '@utils/eventStream';

const { useState, useReducer, useEffect, useRef } = React;

export const Client = () => {
    const queryClient = useQueryClient();
    const slideRef = useRef(null);
    const leadId = parseInt(useParams().leadId);
    const { i18n } = useTranslation();
    const [streaming, setStreaming] = useState(false);
    const [displayWaitingMessage, setDisplayWaitingMessage] = useState(false);
    const [refetchInterval, setRefetchInterval] = useState(false);
    const [errorMsg, setErrorMsg] = useState(false);
    const [sseState, dispatch] = useReducer(clientReducer, initialClientState);
    const [intitialLanguageLoaded, setInitialLanguageLoaded] = useState(false);
    const { DOCUMENTS_PRESENTATION } = PresentationType;

    const clientQueryData = {
        dispatch,
        setDisplayWaitingMessage,
        setErrorMsg,
        refetchInterval,
        setRefetchInterval,
        intitialLanguageLoaded,
        setInitialLanguageLoaded
    };

    const { isLoading, error, data: client } = useClientQuery({ leadId }, clientQueryData);
    const { language } = sseState;

    useEffect(() => {
        i18n.changeLanguage(getAbbreviation(language));
    }, [language]);

    useEffect(() => {
        let stream;

        const { 
            slideIdKey, 
            lastSlideIdKey,
            pointerKey, 
            presentationTypeKey, 
            presentationEndedKey,
            viewportKey,
            presentationIdKey } = BROWSER_STORAGE_KEYS;

        retrieveSavedData([slideIdKey, presentationTypeKey, pointerKey, viewportKey, presentationIdKey, lastSlideIdKey], dispatch);
        retrieveSavedData([presentationEndedKey], dispatch, sessionStorage);

        if (!streaming) {
            const { START, 
                    END,
                    CONNECTION,
                    PRESENTATION_TYPE, 
                    SLIDE_ID, 
                    PDF_PAGE, 
                    SELECT_TERM, 
                    LOAN_INPUTS, 
                    SHOW_POINTER, 
                    POINTER_POSITION, 
                    VIDEO, 
                    LANGUAGE, 
                    STEP, 
                    INFO_ITEM,
                    SCROLL } = SSE_EVENTS;

            const eventStreamQueryParams = {
                leadId,
                connectionType: 'client',
                presentationId: null
            };

            stream = openEventStream(eventStreamQueryParams);
            
            stream.onopen = logOpenMessage();
            stream.onerror = errorHandler;
            stream.addEventListener(CONNECTION, clientConnectionHandler);
            stream.addEventListener(START, startPresentationHandler(dispatch, sseState, queryClient, setRefetchInterval));
            stream.addEventListener(LANGUAGE, languageHandler(dispatch));
            stream.addEventListener(SLIDE_ID, slideIdHandler(dispatch));
            stream.addEventListener(PDF_PAGE, pdfPageHandler(dispatch));
            stream.addEventListener(PRESENTATION_TYPE, presentationTypeHandler(dispatch));
            stream.addEventListener(SELECT_TERM, selectTermHandler(dispatch));
            stream.addEventListener(SHOW_POINTER, showPointerHandler(dispatch));
            stream.addEventListener(POINTER_POSITION, pointerPositionHandler(dispatch));
            stream.addEventListener(VIDEO, videoHandler(dispatch));
            stream.addEventListener(INFO_ITEM, infoItemHandler(dispatch));
            stream.addEventListener(STEP, stepReviewedHandler(dispatch));
            stream.addEventListener(LOAN_INPUTS, loanInputHandler(dispatch));
            stream.addEventListener(SCROLL, scrollHandler(dispatch));
            stream.addEventListener(END, endPresentationHandler(dispatch, leadId, stream));  

            setStreaming(true);
        }

        return () => {
            setStreaming(false);
            stream.close();
        };
    }, []);

    const { 
        presentationType, 
        showPointer, 
        pointerPosition, 
        repConnected,
        presentationEnded,
        presentationId } = sseState;
    
    // TODO: add translation files for these messsages, create reusable loading/error components
    if (isLoading) return <div>Loading...</div>;
    
    if (error && !displayWaitingMessage) {
        return <h2><center>{errorMsg}</center></h2>;
    }

    // Else render the waiting message for the situations we specifically handled to hide from the client. 
    if (!repConnected || displayWaitingMessage) { 
        return <h2>
            <center>Your presentation will begin when your financial consultant is connected.</center>
        </h2>;
    }

    if (repConnected && presentationEnded) {
        return <h2><center>Your presentation has ended. Thank you.</center></h2>;
    }

    return (
        <PresentationProvider
            client={client}
            clientViewing={true}
            profile={getProfile(client.firm)}
            presentationType={presentationType}
            presentationId={presentationId}>
            <IdleTimerContainer />
            <PresentationView
                dispatch={dispatch}
                slideRef={slideRef}
                slideState={sseState}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                {
                    presentationType === DOCUMENTS_PRESENTATION ? 
                    <SinglePagePdf
                        dispatch={dispatch}
                        slideRef={slideRef}
                        slideState={sseState} >
                            <PointerImage 
                                dispatch={dispatch}
                                showPointer={showPointer}
                                position={pointerPosition}/>
                    </SinglePagePdf> 
                    : 
                    <CurrentSlide
                        ref={slideRef}
                        slideState={sseState}
                        pdfStyleActive={false}
                        showScript={false}
                        dispatch={dispatch} >
                            <PointerImage 
                                dispatch={dispatch}
                                showPointer={showPointer}
                                position={pointerPosition}/>
                    </CurrentSlide>
                }
                </ErrorBoundary>
                <ProfileCopyrightFooter profile={getProfile(client.firm)} />
            </PresentationView>
        </PresentationProvider>
    );
};

export default Client;
